<template>
  <div
    class="order-position"
    :class="{
      'order-position--unavailable': !item.availability.available,
      'order-position--free-item': item.freeItem,
      'order-position--recurring-order': item.basketInfo && showFullInfo,
      'order-position--high-current-price': item.price.current.length > 7,
      'order-position--high-summary-price': item.summaryPrice.length > 8
    }"
    :aria-label="`${item.counter.preselectedQuantity}&times; ${item.name} ${item.summaryPrice}`"
  >
    <div
      v-if="item.basketInfo && showFullInfo"
      class="order-position__recurring-order-head"
    >
      {{ $t('checkout.basket.position.recurringOrderItem.head') }}
    </div>

    <div
      v-if="showReduced && item.price.savedPercentage && item.price.savedPercentage < 100 && !item.freeItem"
      class="order-position__reduced-flag"
      role="note"
      :aria-label="`-${item.price.savedPercentage}%`"
    >
      {{ item.price.savedPercentage }}
    </div>

    <component
      :is="disableLink ? 'span' : 'a'"
      :href="disableLink ? null : item.url"
      class="order-position__image-container"
      aria-hidden="true"
    >
      <image-element
        class="order-position__image"
        :class="{
          'product-image--shadow-bottle': isBottle
        }"
        img-class="order-position__image"
        :src-mobile="[item.imageUrlMobile]"
        :src="[item.imageUrl]"
        :alt="item.name"
        :webp="true"
      />
    </component>

    <div class="order-position__info">
      <product-name
        :product-clickable="!disableLink"
        :product="item"
        class="order-position__name"
        :class="{
          'order-position__info--disabled': !item.availability.available
        }"
      />

      <span
        v-if="item.greetingTextCardType !== 'OFF' && showTags"
        class="order-position__greeting-text"
        :class="{
          'order-position__info--disabled': !item.availability.available
        }"
      >
        {{ $t('checkout.basket.position.greetingtext.' + item.greetingTextCardType) }}
      </span>

      <availability-info
        class="order-position__availability"
        :availability="item.availability"
      />

      <div
        v-if="(showTags && (getPromotionTexts().length || item.numberDeliveredInBox)) || item.freeItem"
        class="order-position__tags"
      >
        <div
          class="order-position__promotion order-position__promotion--generic"
          v-for="(promotionTextItem, index) in getPromotionTexts()"
          :key="index"
          v-html="promotionTextItem"
          role="note"
        />
        <span
          v-if="item.freeItem"
          class="order-position__promotion order-position__promotion--free-item"
          v-html="$t('checkout.basket.freeItem.label')"
          role="note"
        />
        <span
          v-if="item.numberDeliveredInBox"
          class="order-position__promotion order-position__promotion--in-box"
          v-html="$t('basket.position.numberDeliveredInBox', [item.numberDeliveredInBox])"
          role="note"
        />
      </div>

      <div
        v-if="showTags && milesAndMoreExcludeText"
        class="order-position__tags"
        role="note"
      >
        <span
          class="order-position__promotion order-position__promotion--miles-and-more"
          v-html="milesAndMoreExcludeText"
        />
      </div>


      <order-position-delivery-select
        v-if="showFullInfo && item.deliveryMode"
        :item="item"
        :disabled="qtyDisabled || !isEditable"
      />

      <div
        v-if="item.soldoutRelation"
        class="order-position__soldout-relation"
      >
        <a
          class="order-position__relation-btn"
          :href="item.soldoutRelation.url"
        >
          {{ $t('ads.ads_top_info.go_to.' + item.soldoutRelation.type) }}
        </a>
      </div>
      <div
        v-else-if="item.availability.available"
        class="order-position__bottom"
      >
        <div class="order-position__price">
          <span
            class="order-position__price-current"
            :class="{'order-position__price-current--discount': item.price.old}"
            :aria-label="$t('checkout.basket.position.header.price')"
          >
            {{ item.price.current }}
          </span>

          <span
            class="order-position__price-old"
            v-if="item.price.old"
            aria-hidden="true"
          >
            {{ item.price.old }}
          </span>
        </div>

        <span class="order-position__qty">
          <counter-input
            :counter="item.counter"
            :disable-input-edit="qtyDisabled || !isEditable"
            @update="changeQuantity($event)"
          />
          <transition name="vue-animation--fade-delayed">
            <div
              class="product-order-position__loader loader"
              v-if="isLoading"
            />
          </transition>
        </span>

        <span
          class="order-position__price-summary"
          :aria-label="$t('checkout.basket.position.header.sum')"
        >
          {{ item.summaryPrice }}
        </span>
      </div>
    </div>

    <div
      class="order-position__recurring-order"
      v-if="showFullInfo"
    >
      <span
        v-if="item.basketInfo"
        class="order-position__product-info"
        :class="{
          'order-position__info--disabled': !item.availability.available
        }"
        v-html="item.basketInfo"
      />
    </div>

    <button
      class="order-position__delete-btn"
      v-if="isEditable"
      @click="deleteItem"
      type="button"
      :aria-label="$t('checkout.basket.removePosition')"
    />
  </div>
</template>

<script setup lang="ts">
  import {defineAsyncComponent, computed, ref} from 'vue';
  import type BasketItem from '../../../bps/types/BasketItem';
  import ImageElement from '../../../bps/components/imageElement/imageElement.vue';
  import AvailabilityInfo from '../../../bps/components/availabilityInfo/availabilityInfo.vue';
  import pigeon from '../../../bps/utilities/js/pigeon/pigeon';
  import basketService from '../../../bps/services/basket/BasketService';
  import {t} from '@/bps/utilities/js/translations/translations';

  const OrderPositionDeliverySelect = defineAsyncComponent(() => import('../../../bps/components/orderPosition/orderPositionDeliverySelect.vue'));

  const props = withDefaults(defineProps<{
    item: BasketItem,
    disableQty?: boolean,
    disableLink?: boolean,
    showReduced?: boolean,
    showTags?: boolean,
    showFullInfo?: boolean,
    isEditable?: boolean
  }>(), {
    disableQty: false,
    disableLink: true,
    showReduced: false,
    showTags: false,
    showFullInfo: false,
    isEditable: true
  });

  const emit = defineEmits<{
    delete: [BasketItem]
  }>();

  const isLoading = ref(false);

  const qtyDisabled = computed(() => {
    return props.disableQty || props.item.freeItem || props.item.greetingCard || props.item.splittedId === '1';
  });

  const isBottle = computed(() => {
    return props.item.price.volumeUnit === 'BOTTLE';
  });

  function deleteItem() {
    emit('delete', props.item);

    if (props.item.freeItem) {
      pigeon.publish('productOrderFreeItem:remove', {product: props.item});
    } else if (props.item.splittedId != null) {
      changeQuantity(0);
    } else {
      isLoading.value = true;
      basketService.removeProduct(props.item, false).then(() => {
        isLoading.value = false;
      });
    }
  }

  async function changeQuantity(qty: number) {
    isLoading.value = true;
    await basketService.changeQuantityOfProduct(props.item, qty);
    isLoading.value = false;
  }

  function getPromotionTexts(): string[] {
    const result = [
      props.item.promotionData.favoriteWineText,
      props.item.promotionData.freeItemsBenefitConditionText,
      props.item.promotionData.freeItemsBenefitAppliedText
    ];

    return result.filter(text => !!text);
  }

  const milesAndMoreExcludeText = computed(() => {
    if (props.item.excludedFromEarn && props.item.excludedForSpent) {
      return t('checkout.basket.mm.excludeFromEarnSpend');
    }
    else if (props.item.excludedFromEarn) {
      return t('checkout.basket.mm.excludeFromEarn');
    }
    else if (props.item.excludedForSpent) {
      return t('checkout.basket.mm.excludeForSpend');
    }

    return '';
  });
</script>
